import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import Keycloak from "keycloak-js";

interface KeycloakContextValue {
  keycloak: Keycloak;
  initialized: boolean;
}

const KeycloakContext = createContext<KeycloakContextValue | undefined>(undefined);

interface KeycloakProviderProps {
  keycloak: Keycloak;
  initOptions?: Keycloak.KeycloakInitOptions;
  children: React.ReactNode;
}

export const KeycloakProvider: React.FC<KeycloakProviderProps> = ({ keycloak, initOptions, children }) => {
  const [initialized, setInitialized] = useState(false);
  const didInit = useRef(false);

  const forceUpdate = useCallback(() => {
    // Trigger a re-render when auth state changes
    setInitialized((prev) => prev);
  }, []);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    keycloak.onAuthSuccess = () => forceUpdate();
    keycloak.onAuthError = () => forceUpdate();
    keycloak.onAuthRefreshSuccess = () => forceUpdate();
    keycloak.onAuthRefreshError = () => forceUpdate();
    keycloak.onAuthLogout = () => forceUpdate();
    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).catch(() => {
        keycloak.login();
      });
    };

    keycloak.init(initOptions || {}).then(() => {
      setInitialized(true);
    }).catch((err) => {
      console.error("Keycloak init failed", err);
      setInitialized(true);
    });
  }, [keycloak, initOptions, forceUpdate]);

  return (
    <KeycloakContext.Provider value={{ keycloak, initialized }}>
      {children}
    </KeycloakContext.Provider>
  );
};

export function useKeycloak() {
  const ctx = useContext(KeycloakContext);
  if (!ctx) {
    throw new Error("useKeycloak must be used within a KeycloakProvider");
  }
  return ctx;
}
